import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Кнопка.
 *
 * Сжатие на нажатии — не украшение: без него интерфейс не подтверждает,
 * что нажатие услышано, и на медленной сети кажется зависшим. При
 * включённом «уменьшить движение» сжатие выключается, цвет остаётся.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap',
    'rounded-lg text-sm font-medium select-none',
    'transition-[background-color,border-color,color,box-shadow,transform,opacity]',
    'duration-150 ease-(--ease-out)',
    'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:border-ring',
    'disabled:pointer-events-none disabled:opacity-45',
    'active:scale-[0.97] motion-reduce:active:scale-100 motion-reduce:transition-none',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        // Градиент, а не плоская заливка: он даёт кнопке направление света
        // сверху вниз и отделяет главное действие от всех остальных.
        default:
          'border border-primary bg-transparent text-primary font-medium pointer-hover:bg-primary/10',
        secondary:
          'border border-(--border-strong) bg-transparent text-foreground pointer-hover:border-primary',
        outline:
          'border border-border bg-transparent text-muted-foreground pointer-hover:border-primary pointer-hover:text-foreground',
        ghost:
          'bg-transparent text-muted-foreground pointer-hover:bg-(--surface-3) pointer-hover:text-foreground',
        // Белая кнопка из макета: используется там, где розовая уже занята
        // соседним действием, а выделить нужно всё равно.
        contrast: 'bg-foreground text-(--ink) font-bold pointer-hover:brightness-110',
        destructive:
          'bg-destructive text-white font-medium pointer-hover:brightness-110',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-9 gap-1.5 px-3 text-[13px]',
        lg: 'h-12 px-6 text-[15px]',
        icon: 'size-10',
        'icon-sm': 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Отрисовать кнопку как переданный элемент — например, как ссылку. */
    asChild?: boolean;
  }): JSX.Element {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
